import Link from 'next/link';
import { FeaturedBlock, HeaderLink } from '@/lib/fetchHeader';

import ChevDown from '@/assets/icon/chev-down.svg';
import Image from 'next/image';
import { generateLink } from '@/components/utils/getLinkTarget';
import { SupportedLanguages } from '@/types/globals';
import { supportedLanguages } from '@/configuration/languages';

// Translate post type labels based on locale
const getTranslatedPostType = (
  postType: string,
  locale: SupportedLanguages,
): string => {
  const config = supportedLanguages[locale];
  if (!config) return postType.toUpperCase();

  const postTypeMap: Record<
    string,
    'blogTitle' | 'newsTitle' | 'eventTitle' | 'caseStudyTitle' | 'careersTitle'
  > = {
    post: 'blogTitle',
    news: 'newsTitle',
    event: 'eventTitle',
    'case-study': 'caseStudyTitle',
    jobs: 'careersTitle',
    page: 'blogTitle', // Default fallback
  };

  const titleKey = postTypeMap[postType];
  if (titleKey && typeof config[titleKey] === 'string') {
    return config[titleKey].toUpperCase();
  }

  // Fallback to original post type if no translation found
  return postType.toUpperCase();
};

export default function SubMenu({
  links,
  title,
  featuredBlocks,
  featuredBlockTitle,
}: {
  links: HeaderLink[];
  title?: string;
  featuredBlocks: FeaturedBlock[];
  featuredBlockTitle: string;
}) {
  return (
    <div className={'grid grid-cols-[1fr_344px] bg-white'}>
      <div className={'flex-1 p-10'}>
        {title && (
          <p className={'mb-10 text-lg font-bold text-indigo-800'}>{title}</p>
        )}
        <div
          className={
            'text-graphite-700 gap-3 font-sans text-[18px] lg:columns-2'
          }
        >
          {links.map(link => (
            <div key={link.id} style={{ breakInside: 'avoid' }}>
              <div
                className={
                  'border-graphite-100 flex items-center gap-2 border-b py-3'
                }
              >
                {!!link.children?.length && <ChevDown />}
                <Link
                  href={generateLink(link.lang, link.type, link.slug)}
                  className={'whitespace-nowrap'}
                >
                  {link.label}
                </Link>
              </div>
              {!!link.children?.length && (
                <ul className={'list-disc pt-3 pl-6'}>
                  {link.children.map(child => (
                    <li key={child.id} className={'mb-3'}>
                      <Link
                        href={generateLink(child.lang, child.type, child.slug)}
                      >
                        {child.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
      {featuredBlocks?.length ? (
        <div className={'text-qf-black flex-shrink-0 bg-[#F7F7FB] p-8'}>
          <p
            className={
              'font-source-serif-pro text-qf-black mb-8 text-[24px] leading-[120%] font-semibold tracking-[-0.014em]'
            }
          >
            {featuredBlockTitle}
          </p>
          <div className={'flex flex-col gap-4'}>
            {featuredBlocks.map(block => (
              <Link
                key={block.id}
                href={generateLink(block.locale, block.post_type, block.slug)}
                className={'flex gap-4'}
              >
                <div
                  className={
                    'bg-graphite-100 relative h-[100px] w-[90px] flex-shrink-0 overflow-hidden rounded-xl'
                  }
                >
                  {block.image && (
                    <Image src={block.image} alt={''} objectFit="cover" fill />
                  )}
                </div>
                <div>
                  <p
                    className={
                      'font-source-sans-pro text-graphite-900 text-[14px] leading-[24px] font-semibold tracking-[0.1em] uppercase'
                    }
                  >
                    {getTranslatedPostType(block.post_type, block.locale)}
                  </p>
                  {block.excerpt && (
                    <div
                      className={
                        'text-graphite-400 font-source-sans-pro line-clamp-3 text-[18px] leading-[120%]'
                      }
                      dangerouslySetInnerHTML={{ __html: block.title }}
                    />
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
